import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Calculator, Clock, TrendingUp, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import LanguageSelector from './LanguageSelector';

const AutomationROICalculator = () => {
  const { t } = useTranslation();
  const [frequency, setFrequency] = useState('weekly');
  const [currentTime, setCurrentTime] = useState<number | ''>(30);
  const [timeSaved, setTimeSaved] = useState<number | ''>(25);
  const [setupTime, setSetupTime] = useState<number | ''>(4);
  const [setupUnit, setSetupUnit] = useState('hours');

  const [results, setResults] = useState<any>({});

  const frequencyMultipliers: Record<string, number> = {
    daily: 365,
    weekly: 52,
    monthly: 12,
    yearly: 1
  };

  const calculateROI = () => {
    // Handle empty or invalid inputs
    const currentTimeNum = Number(currentTime) || 0;
    const timeSavedNum = Number(timeSaved) || 0;
    const setupTimeNum = Number(setupTime) || 0;
    
    if (currentTimeNum <= 0 || timeSavedNum <= 0 || setupTimeNum <= 0) {
      setResults({});
      return;
    }
    
    const yearlyInstances = frequencyMultipliers[frequency];
    const timeSavedPerYear = (timeSavedNum * yearlyInstances) / 60; // Convert to hours
    const setupTimeHours = setupUnit === 'hours' ? setupTimeNum : setupTimeNum / 60;
    
    const breakEvenYears = setupTimeHours / timeSavedPerYear;
    
    const projections: any = {};
    for (let year = 1; year <= 5; year++) {
      const totalTimeSaved = timeSavedPerYear * year;
      const netTimeSaved = totalTimeSaved - setupTimeHours;
      const roi = ((netTimeSaved / setupTimeHours) * 100);
      
      projections[year] = {
        totalSaved: totalTimeSaved,
        netSaved: netTimeSaved,
        roi: roi,
        worthIt: netTimeSaved > 0
      };
    }
    
    setResults({
      breakEvenYears,
      projections,
      setupTimeHours,
      timeSavedPerYear
    });
  };

  useEffect(() => {
    calculateROI();
  }, [frequency, currentTime, timeSaved, setupTime, setupUnit]);

  const getRecommendation = () => {
    if (!results.breakEvenYears) return null;
    
    if (results.breakEvenYears <= 0.5) {
      return {
        type: 'excellent',
        icon: CheckCircle,
        text: t('recommendations.highlyRecommended'),
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        iconColor: 'text-emerald-600'
      };
    } else if (results.breakEvenYears <= 2) {
      return {
        type: 'good',
        icon: AlertCircle,
        text: t('recommendations.worthConsidering'),
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        iconColor: 'text-amber-600'
      };
    } else {
      return {
        type: 'poor',
        icon: XCircle,
        text: t('recommendations.probablyNotWorth'),
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        iconColor: 'text-red-600'
      };
    }
  };

  const recommendation = getRecommendation();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200" role="banner">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center" aria-hidden="true">
                <Calculator className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">{t('title')}</h1>
                <p className="text-sm text-slate-600">{t('subtitle')}</p>
              </div>
            </div>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" role="main">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <section className="space-y-6" aria-labelledby="input-section-title">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle id="input-section-title" className="flex items-center gap-2 text-slate-900">
                  <Clock className="w-5 h-5 text-blue-600" aria-hidden="true" />
                  {t('taskDetails')}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {t('subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Task Frequency */}
                <div className="space-y-2">
                  <label htmlFor="frequency-select" className="text-sm font-medium text-slate-700">
                    {t('frequency.label')}
                  </label>
                  <Select value={frequency} onValueChange={setFrequency}>
                    <SelectTrigger 
                      id="frequency-select"
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                      aria-label={t('frequency.label')}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">{t('frequency.daily')}</SelectItem>
                      <SelectItem value="weekly">{t('frequency.weekly')}</SelectItem>
                      <SelectItem value="monthly">{t('frequency.monthly')}</SelectItem>
                      <SelectItem value="yearly">{t('frequency.yearly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Current Time */}
                <div className="space-y-2">
                  <label htmlFor="current-time" className="text-sm font-medium text-slate-700">
                    {t('currentTime.label')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="current-time"
                      type="number"
                      value={currentTime}
                      onChange={(e) => setCurrentTime(e.target.value === '' ? '' : Number(e.target.value))}
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                      min="1"
                      aria-label={`${t('currentTime.label')} in minutes`}
                      aria-describedby="current-time-unit"
                    />
                    <span className="text-sm text-slate-600 whitespace-nowrap">
                      minutes
                    </span>
                  </div>
                </div>

                {/* Time Saved */}
                <div className="space-y-2">
                  <label htmlFor="time-saved" className="text-sm font-medium text-slate-700">
                    {t('timeSaved.label')}
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="time-saved"
                      type="number"
                      value={timeSaved}
                      onChange={(e) => setTimeSaved(e.target.value === '' ? '' : Number(e.target.value))}
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                      min="1"
                      aria-label={`${t('timeSaved.label')} in minutes`}
                      aria-describedby="time-saved-unit"
                    />
                    <span className="text-sm text-slate-600 whitespace-nowrap">
                      minutes
                    </span>
                  </div>
                </div>

                {/* Setup Time */}
                <div className="space-y-2">
                  <label htmlFor="setup-time" className="text-sm font-medium text-slate-700">
                    {t('setupTime.label')}
                  </label>
                  <div className="flex gap-2">
                    <Input
                      id="setup-time"
                      type="number"
                      value={setupTime}
                      onChange={(e) => setSetupTime(e.target.value === '' ? '' : Number(e.target.value))}
                      className="border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                      min="0.1"
                      step="0.1"
                      aria-label={t('setupTime.label')}
                      aria-describedby="setup-time-unit"
                    />
                    <Select value={setupUnit} onValueChange={setSetupUnit}>
                      <SelectTrigger 
                        id="setup-time-unit"
                        className="w-32 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20"
                        aria-label="Time unit for setup time"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hours">{t('setupTime.hours')}</SelectItem>
                        <SelectItem value="minutes">{t('setupTime.minutes')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Results Section */}
          <section className="space-y-6" aria-labelledby="results-section-title" aria-live="polite">
            {results.breakEvenYears && (
              <>
                {/* Recommendation Card */}
                {recommendation && (
                  <Card className={`border-0 shadow-sm ${recommendation.bgColor} ${recommendation.borderColor} border`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <recommendation.icon className={`w-6 h-6 ${recommendation.iconColor} mt-0.5`} aria-hidden="true" />
                        <div>
                          <h3 id="results-section-title" className="font-medium text-slate-900 mb-1">
                            Recommendation
                          </h3>
                          <p className="text-sm text-slate-700">
                            {recommendation.text}
                          </p>
                          <div className="mt-3">
                            <Badge variant="secondary" className="bg-white/50">
                              {results.breakEvenYears < 1 ? 
                                t('breakEven.months', { count: Math.round(results.breakEvenYears * 12) }) : 
                                t('breakEven.years', { count: results.breakEvenYears.toFixed(1) })
                              } {t('breakEven.toBreakEven')}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Key Metrics */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-slate-900">
                      <TrendingUp className="w-5 h-5 text-orange-600" aria-hidden="true" />
                      {t('breakEvenAnalysis')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-100" role="region" aria-label="Annual time saved">
                        <div className="text-2xl font-bold text-blue-700" aria-label={`${results.timeSavedPerYear.toFixed(1)} hours`}>
                          {results.timeSavedPerYear.toFixed(1)}h
                        </div>
                        <div className="text-sm text-blue-600 mt-1">
                          Annual time saved
                        </div>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 border border-orange-100" role="region" aria-label="Setup investment">
                        <div className="text-2xl font-bold text-orange-700" aria-label={`${results.setupTimeHours.toFixed(1)} hours`}>
                          {results.setupTimeHours.toFixed(1)}h
                        </div>
                        <div className="text-sm text-orange-600 mt-1">
                          Setup investment
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* 5-Year Projection */}
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-4">
                    <CardTitle className="text-slate-900">
                      {t('projections.title')}
                    </CardTitle>
                    <CardDescription className="text-slate-600">
                      5-year time savings and ROI projections
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(results.projections).map(([year, data]: [string, any]) => (
                        <div key={year} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="bg-white">
                              {t('projections.year', { year })}
                            </Badge>
                            <div className="text-sm text-slate-600">
                              {data.netSaved > 0 ? 
                                t('projections.hoursSaved', { hours: data.netSaved.toFixed(1) }) : 
                                t('projections.hoursBehind', { hours: Math.abs(data.netSaved).toFixed(1) })
                              }
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${
                              data.roi > 0 ? 'text-emerald-600' : 'text-red-600'
                            }`}>
                              {data.roi > 0 ? '+' : ''}{data.roi.toFixed(0)}%
                            </span>
                            {data.worthIt ? (
                              <CheckCircle className="w-4 h-4 text-emerald-600" aria-label="Positive ROI" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-600" aria-label="Negative ROI" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {!results.breakEvenYears && (
              <Card className="border-0 shadow-sm border-dashed border-slate-300">
                <CardContent className="pt-6">
                  <div className="text-center py-8">
                    <Calculator className="w-12 h-12 text-slate-400 mx-auto mb-3" aria-hidden="true" />
                    <h3 className="text-lg font-medium text-slate-600 mb-2">
                      Enter your task details
                    </h3>
                    <p className="text-sm text-slate-500">
                      Fill out the form to calculate your automation ROI
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default AutomationROICalculator;
